import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AsyncPipe, Location, TitleCasePipe, DatePipe } from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {MemberStateService} from '../../../services/member-state.service';
import {first, map, switchMap} from 'rxjs/operators';
import {
  ConfirmedElectronicDocument,
  DeclaredSituationEntry,
  ElectronicDocumentDay,
  ElectronicDocumentDetail,
  ElectronicDocumentsService
} from '../../../services/electronic-documents.service';
import {parse, parseISO, isAfter, isEqual, getISODay, subDays, addDays, format} from 'date-fns';
import {Observable} from 'rxjs';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { AlertComponent } from '../../shared/alert/alert.component';
import { KeyValueComponent } from '../../shared/key-value/key-value.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CapitalCasePipe } from '../../../pipes/capital-case.pipe';
import {ViewState, toViewState} from '../../../model/view-state';

interface Comment {
  selectedDays: string[];
  userComment: string;
  commentDate?: string;
}

// Backend calendar day plus the isWeekend flag computed client-side.
interface CalendarDay extends ElectronicDocumentDay {
  isWeekend?: boolean;
}

interface EDocDetailData {
  isElectronicDocumentC3: boolean;
  isElectronicDocumentC32: boolean;
  electronicDocument: ConfirmedElectronicDocument;
  inss: string;
  yearMonth: string;
  confirmationDate: string;
  data: CalendarDay[] | CalendarDay[][];
  comments: Comment[];
}

@Component({
    selector: 'myabvv-e-docs-detail',
    standalone: true,
    templateUrl: './e-docs-detail.component.html',
    imports: [LoaderComponent, AlertComponent, KeyValueComponent, AsyncPipe, TitleCasePipe, DatePipe, TranslatePipe, CapitalCasePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EDocsDetailComponent implements OnInit {
  private location = inject(Location);
  private route = inject(ActivatedRoute);
  private memberStateService = inject(MemberStateService);
  private electronicDocumentService = inject(ElectronicDocumentsService);


  state$: Observable<ViewState<EDocDetailData>>;
  ticketNumber: string;
  startMonthStr: string;
  endMonthStr: string;
  selectedType: string;
  memberInss: string;

  ngOnInit() {
    this.state$ = this.route.queryParams.pipe(
      switchMap(params => {
        this.ticketNumber = params['ticketNumberXL'];
        this.startMonthStr = params['startMonthStr'];
        this.endMonthStr = params['endMonthStr'];
        this.selectedType = params['selectedType'];
        this.memberInss = params['memberInss'];
        return this.memberStateService.getMember();
      }),
      first(),
      switchMap(() => this.electronicDocumentService.getElectronicDocument(this.ticketNumber,
        this.startMonthStr, this.endMonthStr, this.selectedType, this.memberInss)),
      map(electronicDocument => this.buildDetailData(electronicDocument)),
      toViewState()
    );
  }

  onGoBackToOverview() {
    this.location.back();
  }

  private buildDetailData(electronicDocument: ElectronicDocumentDetail | null): EDocDetailData {
    const doc = electronicDocument?.confirmedC3 || electronicDocument?.confirmedC32;
    if (!doc) {
      // Unknown ticket: the backend answers 200 with an empty body.
      throw new Error(`No electronic document returned for ticket ${this.ticketNumber}`);
    }
    const isElectronicDocumentC3 = !!electronicDocument.confirmedC3;
    const isElectronicDocumentC32 = !!electronicDocument.confirmedC32;
    const inss = String(doc.INSS);
    const yearMonth = doc.YearMonth;
    const confirmationDate = this.parseDate(doc.ConfD);
    const comments = this.parseComment(doc.Cmt);
    const data = this.processCalendarDates(doc.Sit, isElectronicDocumentC32);

    return { isElectronicDocumentC3, isElectronicDocumentC32, electronicDocument: doc, inss, yearMonth, confirmationDate, data, comments };
  }

  displayNewCodes(confirmationDate: string) {
    const referenceDate = parse('2024-11-06', 'yyyy-MM-dd', new Date());
    const parsedDate = parseISO(confirmationDate);
    return isAfter(parsedDate, referenceDate) || isEqual(parsedDate, referenceDate);
  }

  getCodeType(confirmationDate: string) {
    return this.displayNewCodes(confirmationDate) ? 'current_codes' : 'ancient_codes';
  }

  hasComments(comments: Comment[]): boolean {
    return comments?.some(comment => comment.selectedDays?.length > 0 || comment.userComment);
  }

  parseDate(dateString: string) {
    let formattedDate;
    if (dateString?.trim()) {
      try {
        formattedDate = new Date(dateString).toISOString().split('T')[0];
      } catch (error) {
        console.error('Failed to format date:', error);
      }
    }
    return formattedDate;
  }

  private parseComment(rawData: string) {
    let parsedArray: Comment[];
    if (rawData?.trim()) {
      try {
        parsedArray = JSON.parse(rawData);
      } catch (error) {
        console.error('Failed to parse JSON:', error);
      }
    }
    return parsedArray;
  }

  private processCalendarDates(data: ElectronicDocumentDay | ElectronicDocumentDay[], isC32: boolean): CalendarDay[] | CalendarDay[][] {
    // XML.toJSONObject collapses a single-day Sit list to a bare object.
    const days: CalendarDay[] = Array.isArray(data) ? data : data ? [data] : [];
    if (days.length === 0) {
      return days;
    }

    let processedData = days;

    if (isC32) {
      processedData = this.normalizeC32Documents(processedData);
      processedData.forEach((d: CalendarDay) => {
        if (d.Date.includes('+')) {
          d.Date = d.Date.substring(0, d.Date.length - 6);
        }
      });
    }

    let result: {Date: string; isWeekend?: boolean}[] = [];
    const previousMonthDates: {Date: string}[] = [];
    const nextMonthDates: {Date: string}[] = [];
    let firstDate = parseISO(processedData[0].Date);
    let lastDate = parseISO(processedData[processedData.length - 1].Date);

    while (getISODay(firstDate) !== 1 || getISODay(lastDate) !== 7) {
      if (getISODay(firstDate) !== 1) {
        firstDate = subDays(firstDate, 1);
        previousMonthDates.push({Date: format(firstDate, 'yyyy-MM-dd')});
      }

      if (getISODay(lastDate) !== 7) {
        lastDate = addDays(lastDate, 1);
        nextMonthDates.push({Date: format(lastDate, 'yyyy-MM-dd')});
      }
    }

    previousMonthDates.reverse();
    result = result.concat(previousMonthDates, processedData, nextMonthDates);

    result.forEach(r => {
      const weekday = getISODay(parseISO(r.Date));
      r.isWeekend = weekday === 6 || weekday === 7;
    });
    return this.chunkArray(result);
  }

  private chunkArray(arr: CalendarDay[]): CalendarDay[][] { // split array into chunks of 7 to present it onto the calendar
    const result: CalendarDay[][] = [];

    while (arr.length) {
      result.push(arr.splice(0, 7));
    }

    return result;
  }

  private normalizeC32Documents(arr: CalendarDay[]): CalendarDay[] {
    return arr.map((item: CalendarDay) => {
      let situations: DeclaredSituationEntry[] = [];
      const situationField = item.DeclaredSituation?.Situation;

      if (Array.isArray(situationField)) {
        situations = situationField;
      } else if (situationField) {
        situations = [situationField];
      }

      return {
        DeclaredSituation: { Situation: situations },
        Date: item.Date
      };
    });
  }
}
